/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import type { GridColDef } from '@mui/x-data-grid';
import { ApiBulkSession } from '@agunity/api-v4';

import AgTable from '../../stories/AgTable';
import AgTypography from '../../stories/Styles/AgTypography';
import AgNoPermission from '../../components/AgNoPermission';

import { formatDateTime } from '../../utils';
import { getBulkUploadRequests } from './redux';
import { selectFilteredUsers } from '../../redux/slices/global';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { BULK_UPLOAD_REQUEST_READ_PERMISSION, ROOT_PATHS } from '../../Constants';
import * as S from './styled';

const BulkUploadRequest = () => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const dispatch = useAppDispatch();
  const users = useAppSelector(selectFilteredUsers);

  const [bulkUploadRequests, setBulkUploadRequests] = useState<ApiBulkSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState();

  const permissions = (users?.length && users[0].permissions) || [];
  const canRead = permissions.includes(BULK_UPLOAD_REQUEST_READ_PERMISSION);

  const fetchBulkUploadRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatch(getBulkUploadRequests());
      setBulkUploadRequests(data || []);
    } catch (error) {
      console.error('Failed to fetch bulkUploadRequests:', error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const handleViewDetails = useCallback(() => {
    if (selectedRow) {
      const id = selectedRow + 1;
      const key = bulkUploadRequests.find((request) => request.id === id)?.key;
      navigate(`${ROOT_PATHS.BULK_UPLOAD_REQUEST}/${key}`);
    };
  }, [navigate, bulkUploadRequests, selectedRow]);

  const columns = useMemo(
    () =>
      [
        {
          field: 'id',
          headerName: formatMessage({ id: 'BULK_UPLOAD_REQUEST_ID' }),
          valueGetter: ({ value }) => value ? value : '',
        },
        {
          field: 'source',
          headerName: formatMessage({ id: 'BULK_UPLOAD_REQUEST_SOURCE' }),
          valueGetter: ({ value }) => formatMessage({ id: `BULK_UPLOAD_REQUEST_SOURCE_${value}` })
        },
        {
          field: 'userEmail',
          headerName: formatMessage({ id: 'BULK_UPLOAD_REQUEST_REQUESTER' }),
          minWidth: 264,
        },
        {
          field: 'createdAsOf',
          headerName: formatMessage({ id: 'BULK_UPLOAD_REQUEST_DATETIME_REQUESTED' }),
          minWidth: 150,
          valueGetter: ({ value }) => value ? formatDateTime(value) : '',
        },
        {
          field: 'state',
          headerName: formatMessage({ id: 'BULK_UPLOAD_REQUEST_STATUS' }),
          minWidth: 150,
          valueGetter: ({ value }) => formatMessage({ id: `BULK_UPLOAD_REQUEST_STATE_${value}` })
        },
        {
          field: 'resultState',
          headerName: formatMessage({ id: 'BULK_UPLOAD_REQUEST_RESULT' }),
          minWidth: 150,
          valueGetter: ({ value }) => formatMessage({ id: `BULK_UPLOAD_REQUEST_RESULT_STATE_${value}` })
        },
        {
          field: 'updatedAsOf',
          headerName: formatMessage({ id: 'BULK_UPLOAD_REQUEST_DATETIME_UPDATED' }),
          minWidth: 150,
          valueGetter: ({ value }) => value ? formatDateTime(value) : '',
        },
      ] as GridColDef[],
    [formatMessage]
  );

  useEffect(() => {
    fetchBulkUploadRequests();
  }, [fetchBulkUploadRequests]);

  useEffect(() => {
    handleViewDetails();
  }, [handleViewDetails]);

  if (!canRead) return <AgNoPermission />;

  return (
    <S.Main>
      <AgTypography
        message={formatMessage({ id: 'BULK_UPLOAD_REQUEST' })}
        contentStyle={'AP.H2.Bold'}
        component='h2'
      />

      <S.Container>
        <AgTable
          showToolbar
          type='generic'
          disabledCheckbox
          columns={columns}
          initialRows={bulkUploadRequests}
          tableLoading={isLoading}
          setSelectedRowIndex={setSelectedRow}
          addNewButtonDisabled
          hasAddNewButton={false}
          setSort={() => { }}
          allowToSelectBoolean
          showTableMenu={false}
          noRowsOverlayContent={formatMessage({ id: 'GENERIC_NO_TABLE_ROWS' })}
        />
      </S.Container>
    </S.Main>
  );
};

export default BulkUploadRequest;
